import { useState } from 'react'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import { firebaseAuth, firestoreDb } from '../services/firebase'
import { deleteUserAccount } from '../utils/destructiveOps'
import { AppDialog } from './Common/AppDialog'
import { TextInput } from './Common'
import { useNls } from '../nls/nlsProvider'
import { formatPurgeDate } from '../utils/purgeDate'

type DeleteAccountModalProps = {
  userId: string
  onClose: () => void
}

export const DeleteAccountModal = ({ userId, onClose }: DeleteAccountModalProps) => {
  const { t } = useNls()
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [scheduledMessage, setScheduledMessage] = useState<string | null>(null)
  const requiredConfirmation = 'delete'
  const canConfirm = confirmation === requiredConfirmation

  const handleDelete = async () => {
    if (!canConfirm) return

    setError(null)
    setIsDeleting(true)

    try {
      const res = await deleteUserAccount(userId, firestoreDb, firebaseAuth)
      const dateStr = formatPurgeDate(res?.purgeAfter)
      const msg = t('NLSDeletionScheduledGrace', { date: dateStr })
      setScheduledMessage(msg)
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Could not delete account.'
      setError(
        message.includes('auth/requires-recent-login') || message.includes('requires-recent-login')
          ? 'For your security, please log out, sign in again, and retry account deletion.'
          : message,
      )
      setIsDeleting(false)
    }
  }

  if (scheduledMessage) {
    return (
      <AppDialog
        open
        title="Delete Account"
        tone="danger"
        icon={<DeleteForeverRoundedIcon />}
        onDismiss={onClose}
        size="sm"
        actions={[
          {
            label: 'OK',
            variant: 'primary',
            onClick: onClose,
          },
        ]}
      >
        <p className="scheduledDeletionNotice" style={{ margin: '12px 0' }}>{scheduledMessage}</p>
      </AppDialog>
    )
  }

  return (
    <AppDialog
      open
      title="Delete Account"
      tone="danger"
      icon={<DeleteForeverRoundedIcon />}
      onDismiss={onClose}
      size="sm"
      actions={[
        {
          label: 'Cancel',
          variant: 'ghost',
          onClick: onClose,
          disabled: isDeleting,
        },
        {
          label: isDeleting ? 'Deleting' : 'Delete Account',
          variant: 'danger',
          onClick: handleDelete,
          disabled: !canConfirm,
          loading: isDeleting,
        },
      ]}
    >
      <p className="dropdownLabel destructiveLabel">Permanent action</p>
      <p className="destructiveWarning">
        This will permanently delete your account, all workspaces you own, and all associated timelines.
      </p>
      <label htmlFor="deleteAccountConfirm" className="destructiveInstruction">
        TYPE <strong>"{requiredConfirmation}"</strong> TO DELETE
      </label>
      <TextInput
        id="deleteAccountConfirm"
        value={confirmation}
        onChange={setConfirmation}
        placeholder="delete"
        autoFocus
        error={confirmation.length > 0 && !canConfirm}
      />
      {error && <p className="authError">{error}</p>}
    </AppDialog>
  )
}
